import * as Plot from '@observablehq/plot';
import type { CustomExperimentSpec, PanelRow } from '@learnforge/demo-render/harness';
import { defineExperiment } from '@learnforge/demo-render/harness';
import { createChartCard } from '../render/chart-card';
import {
  createDataTable,
  type DataTableColumn,
  type DataTableHandle,
  type DataTableStat,
} from '@learnforge/demo-kit';
import { MASTER_SURVEY, type StudentRow } from '../data/class-survey';
import {
  ICE_CREAM_VS_DROWNINGS,
  STORKS_VS_BIRTHS,
  SLEEP_VS_REACTION,
  type BivariateDataset,
  type BivariatePoint,
} from '../data';
import { pearsonR, linearRegression } from '../statistics';
import { formatNumber } from '@learnforge/demo-math/numeric';
import type { StatsContext, DemoKey } from '../types';

// ---------------------------------------------------------------------------
// Quantitative column keys that may be used as x / y in master-survey
// ---------------------------------------------------------------------------

type SurveyNumericKey = 'height_cm' | 'shoe_size_eu' | 'study_hours' | 'test_score' | 'commute_min';

const SURVEY_NUMERIC_COLUMNS: { key: SurveyNumericKey; labelKey: DemoKey }[] = [
  { key: 'height_cm', labelKey: 'column.height_cm.label' },
  { key: 'shoe_size_eu', labelKey: 'column.shoe_size_eu.label' },
  { key: 'study_hours', labelKey: 'column.study_hours.label' },
  { key: 'test_score', labelKey: 'column.test_score.label' },
  { key: 'commute_min', labelKey: 'column.commute_min.label' },
];

function columnLabelKey(key: SurveyNumericKey): DemoKey {
  return SURVEY_NUMERIC_COLUMNS.find((c) => c.key === key)!.labelKey;
}

const DEFAULT_X_KEY: SurveyNumericKey = 'study_hours';
const DEFAULT_Y_KEY: SurveyNumericKey = 'test_score';

// ---------------------------------------------------------------------------
// Unified dataset descriptor used internally
// ---------------------------------------------------------------------------

interface DatasetMeta {
  id: string;
  labelKey: DemoKey;
  descKey: DemoKey;
  kind: 'master-survey' | 'preset';
  preset?: BivariateDataset;
}

const DATASETS: DatasetMeta[] = [
  {
    id: 'master-survey',
    labelKey: 'dataset.master-survey.label',
    descKey: 'dataset.master-survey.desc',
    kind: 'master-survey',
  },
  {
    id: ICE_CREAM_VS_DROWNINGS.id,
    labelKey: ICE_CREAM_VS_DROWNINGS.labelKey,
    descKey: ICE_CREAM_VS_DROWNINGS.descKey,
    kind: 'preset',
    preset: ICE_CREAM_VS_DROWNINGS,
  },
  {
    id: STORKS_VS_BIRTHS.id,
    labelKey: STORKS_VS_BIRTHS.labelKey,
    descKey: STORKS_VS_BIRTHS.descKey,
    kind: 'preset',
    preset: STORKS_VS_BIRTHS,
  },
  {
    id: SLEEP_VS_REACTION.id,
    labelKey: SLEEP_VS_REACTION.labelKey,
    descKey: SLEEP_VS_REACTION.descKey,
    kind: 'preset',
    preset: SLEEP_VS_REACTION,
  },
];

// ---------------------------------------------------------------------------
// State — model only; card/table/DOM refs are transient (held in createRender closure)
// ---------------------------------------------------------------------------

interface State {
  activeDatasetId: string;
  xKey: SurveyNumericKey;
  yKey: SurveyNumericKey;
  showRegression: boolean;
  showResiduals: boolean;
  showConfounder: boolean;
}

// ---------------------------------------------------------------------------
// Spec factory
// ---------------------------------------------------------------------------

function buildSpec(): CustomExperimentSpec<State, DemoKey, StatsContext> {
  return {
    labelKey: 'exp.two-variable-lab.title',
    demoId: 'statistics',
    advancedKey: 'two-variable-lab',

    initialState(): State {
      return {
        activeDatasetId: 'master-survey',
        xKey: DEFAULT_X_KEY,
        yKey: DEFAULT_Y_KEY,
        showRegression: true,
        showResiduals: false,
        showConfounder: false,
      };
    },

    panel: (state): PanelRow<DemoKey, State>[] => {
      const meta = DATASETS.find((d) => d.id === state.activeDatasetId) ?? DATASETS[0]!;
      const isMasterSurvey = meta.kind === 'master-survey';

      const rows: PanelRow<DemoKey, State>[] = [
        {
          kind: 'dropdown',
          id: 'tv-dataset',
          labelKey: 'param.dataset.label',
          options: DATASETS.map((d) => ({ value: d.id, labelKey: d.labelKey })),
          value: state.activeDatasetId,
          onChange: (v) => (prev) => {
            const nextMeta = DATASETS.find((d) => d.id === v) ?? DATASETS[0]!;
            return {
              ...prev,
              activeDatasetId: v,
              // Reset xy selection when switching to master-survey; presets fix them.
              ...(nextMeta.kind === 'master-survey'
                ? { xKey: DEFAULT_X_KEY, yKey: DEFAULT_Y_KEY }
                : {}),
              showConfounder: false,
            };
          },
        },
      ];

      if (isMasterSurvey) {
        rows.push(
          {
            kind: 'dropdown',
            id: 'tv-x-col',
            labelKey: 'param.x-col.label',
            options: SURVEY_NUMERIC_COLUMNS.map((c) => ({ value: c.key, labelKey: c.labelKey })),
            value: state.xKey,
            onChange: (v) => (prev) => ({ ...prev, xKey: v as SurveyNumericKey }),
          },
          {
            kind: 'dropdown',
            id: 'tv-y-col',
            labelKey: 'param.y-col.label',
            options: SURVEY_NUMERIC_COLUMNS.map((c) => ({ value: c.key, labelKey: c.labelKey })),
            value: state.yKey,
            onChange: (v) => (prev) => ({ ...prev, yKey: v as SurveyNumericKey }),
          },
        );
      }

      rows.push(
        {
          kind: 'toggle',
          labelKey: 'param.show-regression.label',
          value: state.showRegression,
          onChange: (v) => (prev) => ({ ...prev, showRegression: v }),
        },
        {
          kind: 'toggle',
          labelKey: 'param.show-residuals.label',
          value: state.showResiduals,
          onChange: (v) => (prev) => ({ ...prev, showResiduals: v }),
        },
      );

      if (meta.preset?.confounderLabelKey) {
        rows.push({
          kind: 'toggle',
          labelKey: 'param.show-confounder.label',
          value: state.showConfounder,
          onChange: (v) => (prev) => ({ ...prev, showConfounder: v }),
        });
      }

      return rows;
    },

    createRender(ctx, host) {
      // ── Stage grid wrapper ──
      const grid = document.createElement('div');
      grid.className = 'stats-grid';
      host.appendChild(grid);

      // `getStats()` below (the data table's Σ popover) reads these on every
      // table refresh; `update()` keeps them in sync with the live x/y
      // selection before calling `dataTable.update()` (mirrors the
      // currentColumnKey guard used by survey-explorer/distribution-sculptor).
      let currentXs: number[] = [];
      let currentYs: number[] = [];
      let currentDegenerate = false;

      function computeStats(): DataTableStat[] {
        const n = currentXs.length;
        if (currentDegenerate) {
          return [{ label: ctx.i18n.t('stat.n'), value: String(n) }];
        }
        const r = pearsonR(currentXs, currentYs);
        const reg = linearRegression(currentXs, currentYs);
        return [
          {
            label: ctx.i18n.t('stat.r'),
            value: formatNumber(r, { decimals: 4, trimIntegers: true }),
          },
          {
            label: ctx.i18n.t('stat.r2'),
            value: formatNumber(reg.rSquared, { decimals: 4, trimIntegers: true }),
          },
          {
            label: ctx.i18n.t('stat.slope'),
            value: formatNumber(reg.slope, { decimals: 4, trimIntegers: true }),
          },
          {
            label: ctx.i18n.t('stat.intercept'),
            value: formatNumber(reg.intercept, { decimals: 4, trimIntegers: true }),
          },
          { label: ctx.i18n.t('stat.n'), value: String(n) },
        ];
      }

      // Build the data table for a given dataset — columns differ between
      // master-survey (all numeric columns, name column, picker) and a preset
      // (fixed x/y/confounder columns, no picker).
      function buildTable(
        meta: DatasetMeta,
        xKey: SurveyNumericKey,
        yKey: SurveyNumericKey,
      ): DataTableHandle {
        if (meta.kind === 'master-survey') {
          const columns: DataTableColumn<DemoKey>[] = [
            { id: 'name', labelKey: 'column.name.label' },
            ...SURVEY_NUMERIC_COLUMNS.map((c) => ({ id: c.key, labelKey: c.labelKey })),
          ];
          return createDataTable<DemoKey>({
            i18n: ctx.i18n,
            columns,
            getRows: () => MASTER_SURVEY.rows as unknown as ReadonlyArray<Record<string, unknown>>,
            getStats: computeStats,
            defaultVisible: [xKey, yKey],
            rowNumbers: true,
          });
        }
        const preset = meta.preset!;
        const columns: DataTableColumn<DemoKey>[] = [
          { id: 'x', labelKey: preset.xLabelKey },
          { id: 'y', labelKey: preset.yLabelKey },
        ];
        if (preset.confounderLabelKey) {
          columns.push({ id: 'confounder', labelKey: preset.confounderLabelKey });
        }
        return createDataTable<DemoKey>({
          i18n: ctx.i18n,
          columns,
          getRows: () => preset.points as unknown as ReadonlyArray<Record<string, unknown>>,
          getStats: computeStats,
          rowNumbers: true,
        });
      }

      const initialMeta = DATASETS[0]!; // master-survey
      let dataTable = buildTable(initialMeta, DEFAULT_X_KEY, DEFAULT_Y_KEY);

      // ── Three cards (built once) — DOM order: data table, scatter, description ──
      const tableCard = createChartCard<DemoKey>({
        titleKey: 'table.data',
        i18n: ctx.i18n,
        headerActions: [dataTable.controls],
        collapsible: { storageKey: 'learnforge:collapsible:statistics:two-variable-lab:table' },
      });
      const scatterCard = createChartCard<DemoKey>({
        titleKey: 'card.scatter',
        i18n: ctx.i18n,
        collapsible: { storageKey: 'learnforge:collapsible:statistics:two-variable-lab:scatter' },
      });
      const descCard = createChartCard<DemoKey>({
        titleKey: 'card.description',
        i18n: ctx.i18n,
        collapsible: {
          storageKey: 'learnforge:collapsible:statistics:two-variable-lab:description',
        },
      });

      grid.append(tableCard.element, scatterCard.element, descCard.element);
      tableCard.setBody(dataTable.element);

      // `headerActions` is constructor-only on createChartCard. When the dataset
      // switches (master-survey <-> preset have different columns) we destroy +
      // recreate the table and manually swap its `controls` into this div,
      // rather than rebuilding the card — which would drop its collapse state.
      const tableActions = tableCard.element.querySelector<HTMLElement>('.chart-card-actions')!;

      let tableDatasetId = initialMeta.id;
      let tableXKey: SurveyNumericKey = DEFAULT_X_KEY;
      let tableYKey: SurveyNumericKey = DEFAULT_Y_KEY;

      // ── Update: re-render the 3 cards from state ──
      function update(state: State): void {
        const { activeDatasetId, xKey, yKey, showRegression, showResiduals, showConfounder } =
          state;

        const meta = DATASETS.find((d) => d.id === activeDatasetId) ?? DATASETS[0]!;
        const points = resolvePoints(meta, xKey, yKey);
        const xs = points.map((p) => p.x);
        const ys = points.map((p) => p.y);
        const n = points.length;
        const sameColumns = meta.kind === 'master-survey' && xKey === yKey;
        const degenerate = sameColumns || n < 3;

        currentXs = xs;
        currentYs = ys;
        currentDegenerate = degenerate;

        // Dataset switch: columns differ → rebuild the table.
        if (activeDatasetId !== tableDatasetId) {
          dataTable.destroy();
          dataTable = buildTable(meta, xKey, yKey);
          tableCard.setBody(dataTable.element);
          tableActions.replaceChildren(dataTable.controls);
          tableDatasetId = activeDatasetId;
          tableXKey = xKey;
          tableYKey = yKey;
        } else if (meta.kind === 'master-survey' && (xKey !== tableXKey || yKey !== tableYKey)) {
          dataTable.setVisible([xKey, yKey]);
          tableXKey = xKey;
          tableYKey = yKey;
        }
        dataTable.update();

        // Scatter card
        if (sameColumns) {
          scatterCard.setBody(makeMsgEl(ctx.i18n.t('message.same-columns')));
        } else if (n < 3) {
          scatterCard.setBody(makeMsgEl(ctx.i18n.t('message.n-too-small')));
        } else {
          const xLabel =
            meta.kind === 'master-survey'
              ? ctx.i18n.t(columnLabelKey(xKey))
              : ctx.i18n.t(meta.preset!.xLabelKey);
          const yLabel =
            meta.kind === 'master-survey'
              ? ctx.i18n.t(columnLabelKey(yKey))
              : ctx.i18n.t(meta.preset!.yLabelKey);
          scatterCard.setBody(
            renderScatter(
              points,
              xs,
              ys,
              showRegression,
              showResiduals,
              showConfounder && !!meta.preset?.confounderLabelKey,
              xLabel,
              yLabel,
            ),
          );
        }

        // Description card
        descCard.setBody(renderDesc(ctx.i18n.t(meta.descKey)));

        // CRITICAL: signal VR readiness on all 3 cards after every render
        for (const c of [tableCard, scatterCard, descCard]) {
          c.setBusy(false);
        }
      }

      return {
        update,
        dispose() {
          dataTable.destroy();
          tableCard.destroy();
          scatterCard.destroy();
          descCard.destroy();
        },
      };
    },

    applyShotState(state): State {
      return state;
    },
  };
}

export const experiment = defineExperiment(buildSpec());

// ---------------------------------------------------------------------------
// Registry/test alias — index.ts imports this name
// ---------------------------------------------------------------------------

export const mountTwoVariableLab = experiment.mount.bind(experiment);

// ---------------------------------------------------------------------------
// Resolve points from dataset meta + column selection
// ---------------------------------------------------------------------------

function resolvePoints(
  meta: DatasetMeta,
  xKey: SurveyNumericKey,
  yKey: SurveyNumericKey,
): BivariatePoint[] {
  if (meta.kind === 'preset' && meta.preset) {
    return meta.preset.points as BivariatePoint[];
  }
  // master-survey
  return (MASTER_SURVEY.rows as StudentRow[]).map((row) => ({
    x: (row as unknown as Record<string, number>)[xKey] ?? 0,
    y: (row as unknown as Record<string, number>)[yKey] ?? 0,
  }));
}

// ---------------------------------------------------------------------------
// Scatter plot renderer
// ---------------------------------------------------------------------------

function renderScatter(
  points: BivariatePoint[],
  xs: number[],
  ys: number[],
  showRegression: boolean,
  showResiduals: boolean,
  showConfounder: boolean,
  xLabel: string,
  yLabel: string,
): HTMLElement | SVGSVGElement {
  const marks: Plot.Markish[] = [Plot.frame({ anchor: 'left' }), Plot.frame({ anchor: 'bottom' })];

  if (showConfounder) {
    marks.push(
      Plot.dot(points, {
        x: (d: BivariatePoint) => d.x,
        y: (d: BivariatePoint) => d.y,
        fill: (d: BivariatePoint) => String(d.confounder ?? ''),
      }),
    );
  } else {
    marks.push(
      Plot.dot(points, {
        x: (d: BivariatePoint) => d.x,
        y: (d: BivariatePoint) => d.y,
      }),
    );
  }

  if (showRegression) {
    marks.push(
      Plot.linearRegressionY(points, {
        x: (d: BivariatePoint) => d.x,
        y: (d: BivariatePoint) => d.y,
      }),
    );
  }

  if (showResiduals && xs.length >= 2) {
    const reg = linearRegression(xs, ys);
    if (!Number.isNaN(reg.slope)) {
      const residualData = points.map((p) => ({
        x: p.x,
        y1: p.y,
        y2: reg.slope * p.x + reg.intercept,
      }));
      marks.push(
        Plot.link(residualData, {
          x1: (d: { x: number; y1: number; y2: number }) => d.x,
          y1: (d: { x: number; y1: number; y2: number }) => d.y1,
          x2: (d: { x: number; y1: number; y2: number }) => d.x,
          y2: (d: { x: number; y1: number; y2: number }) => d.y2,
          stroke: 'var(--accent-tertiary)',
          strokeOpacity: 0.7,
        }),
      );
    }
  }

  return Plot.plot({
    marks,
    height: 300,
    x: { label: xLabel },
    y: { label: yLabel },
  }) as HTMLElement | SVGSVGElement;
}

// ---------------------------------------------------------------------------
// Description card renderer
// ---------------------------------------------------------------------------

function renderDesc(text: string): HTMLElement {
  const p = document.createElement('p');
  p.className = 'tv-desc';
  p.textContent = text;
  return p;
}

// ---------------------------------------------------------------------------
// Degenerate / error message element
// ---------------------------------------------------------------------------

function makeMsgEl(msg: string): HTMLElement {
  const div = document.createElement('div');
  div.className = 'empty-card-explainer';
  div.textContent = msg;
  return div;
}
